import {
  useListManualPaymentSlips,
  useListTaxPaymentSlips,
} from '@/api/accounting-dealer'
import { useCompany } from '@/context/companies'
import { capitalize, formatCurrency, formatDate } from '@/helpers'
import {
  ManualPaymentSlip,
  TAX_PAYMENT_SLIP_TYPE_ORDER,
  TaxPaymentSlip,
} from '@/types'
import { ErrorOutline } from '@mui/icons-material'
import { Box, Skeleton, Stack, Typography } from '@mui/material'
import { useMemo } from 'react'
import { ManualPaymentSlipCard } from './manual-payment-slip-card'
import { TaxPaymentSlipCard } from './tax-payment-slip-card'

type GuideRow =
  | {
      dueDay: number
      dueMonth: number
      dueYear: number
      id: string
      kind: 'manual'
      slip: ManualPaymentSlip
    }
  | {
      dueDay: number
      dueMonth: number
      dueYear: number
      id: string
      kind: 'tax'
      slip: TaxPaymentSlip
    }

type TaxPaymentSlipListProps = {
  month?: number
  paid?: boolean
  year?: number
}

function taxDueParts(slip: TaxPaymentSlip) {
  if (slip.due_date) return dateParts(slip.due_date)
  const dueMonth = slip.month === 12 ? 1 : slip.month + 1
  const dueYear = slip.month === 12 ? slip.year + 1 : slip.year
  return { dueDay: 20, dueMonth, dueYear }
}

function dateParts(value: string) {
  const [dueYear, dueMonth, dueDay] = value.slice(0, 10).split('-').map(Number)
  return { dueDay, dueMonth, dueYear }
}

export function TaxPaymentSlipList({
  month,
  paid,
  year,
}: TaxPaymentSlipListProps) {
  const { company } = useCompany()
  const filters = {
    company_key: company.key,
    page_size: 500,
    paid,
    ...(month !== undefined ? { due_month: month } : {}),
    ...(year !== undefined ? { due_year: year } : {}),
  }

  const taxSlips = useListTaxPaymentSlips(filters)
  const manualSlips = useListManualPaymentSlips(filters)

  const groups = useMemo(() => {
    const rows: GuideRow[] = [
      ...(taxSlips.data?.data ?? []).map((slip) => ({
        ...taxDueParts(slip),
        id: `tax-${slip.key}`,
        kind: 'tax' as const,
        slip,
      })),
      ...(manualSlips.data?.data ?? []).map((slip) => ({
        ...dateParts(slip.due_date),
        id: `manual-${slip.key}`,
        kind: 'manual' as const,
        slip,
      })),
    ]

    const grouped = rows.reduce<Record<string, GuideRow[]>>((acc, row) => {
      const key = `${row.dueYear}-${row.dueMonth}`
      acc[key] = acc[key] ?? []
      acc[key].push(row)
      return acc
    }, {})

    return Object.values(grouped)
      .map((monthRows) => {
        const sorted = [...monthRows].sort((left, right) => {
          if (left.dueDay !== right.dueDay) return right.dueDay - left.dueDay
          if (left.kind !== right.kind) return left.kind === 'tax' ? -1 : 1
          if (left.kind === 'tax' && right.kind === 'tax') {
            return (
              TAX_PAYMENT_SLIP_TYPE_ORDER[left.slip.type] -
              TAX_PAYMENT_SLIP_TYPE_ORDER[right.slip.type]
            )
          }
          return left.slip.type.localeCompare(right.slip.type)
        })
        const total = sorted.reduce((sum, row) => {
          const amount =
            row.kind === 'tax'
              ? Number(row.slip.document_amount ?? row.slip.amount)
              : Number(row.slip.amount)
          return sum + amount
        }, 0)

        return {
          dueMonth: sorted[0].dueMonth,
          dueYear: sorted[0].dueYear,
          rows: sorted,
          total,
        }
      })
      .sort((left, right) => {
        if (left.dueYear !== right.dueYear) return right.dueYear - left.dueYear
        return right.dueMonth - left.dueMonth
      })
  }, [manualSlips.data, taxSlips.data])

  if (taxSlips.isLoading || manualSlips.isLoading) {
    return (
      <Stack spacing={2}>
        {Array.from({ length: 2 }).map((_, index) => (
          <Stack key={index} spacing={1}>
            <Skeleton sx={{ fontSize: 28 }} width={180} />
            <Skeleton variant="rounded" height={110} />
            <Skeleton variant="rounded" height={110} />
          </Stack>
        ))}
      </Stack>
    )
  }

  if (taxSlips.isError || manualSlips.isError) {
    return (
      <Box textAlign="center">
        <ErrorOutline color="error" fontSize="large" />
        <Typography color="gray">
          Não foi possível carregar as guias de imposto.
        </Typography>
      </Box>
    )
  }

  if (groups.length === 0) {
    return (
      <Typography color="gray" textAlign="center" py={2}>
        Não encontramos guias
      </Typography>
    )
  }

  return (
    <Stack spacing={3}>
      {groups.map(({ dueMonth, dueYear, rows, total }) => {
        const dueMonthDate = new Date(Date.UTC(dueYear, dueMonth - 1, 1))

        return (
          <Box key={`${dueYear}-${dueMonth}`}>
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              mb={2}
            >
              <Typography variant="h5">
                {capitalize(formatDate(dueMonthDate, 'MMMM yyyy'))}
              </Typography>
              <Typography variant="h4">{formatCurrency(total)}</Typography>
            </Box>

            <Stack spacing={1}>
              {rows.map((row) =>
                row.kind === 'tax' ? (
                  <TaxPaymentSlipCard key={row.id} slip={row.slip} />
                ) : (
                  <ManualPaymentSlipCard key={row.id} slip={row.slip} />
                ),
              )}
            </Stack>
          </Box>
        )
      })}
    </Stack>
  )
}
